// Catch the escapee: the sheet a bird that got out of the run can be caught
// from (REFOCUS §12).
//
// It is a sheet, not a route, for the same reason the chore sheet is: the yard
// stays alive behind the backdrop and the escapee is still out there while the
// player reads how hard she is to reach.
//
// The odds are the sim's. This file asks `catchEscapee` for one attempt and
// reports what came back; it has no dice of its own.

import { t } from '../../i18n';
import { catchEscapee } from '../actions';
import { playChoreDone } from '../sound';
import { openSheet } from '../components/sheet';
import { button, div, h, icon } from '../dom';
import { selectEscapee, type EscapeeView } from '../selectors';
import type { ScreenContext } from '../router';

const CATCH_ICON = '<path d="M5 12h14"></path><path d="M15 7l5 5-5 5"></path>';

/**
 * Opens the catch sheet for one bird. Not a route: the coop screen stays
 * mounted behind it, and a second escapee is still waiting in the banner.
 */
export function openCatchSheet(ctx: ScreenContext<HTMLElement>, chickenId: string): void {
  const { store, shell } = ctx.services;
  const view = selectEscapee(store.getState(), chickenId);
  const sheet = openSheet(shell, {
    title: view ? t('escapee.title', { name: view.name }) : t('escapee.titleGone'),
    onClose: () => undefined,
  });

  if (!view) {
    // She was caught, or the save was reloaded, between the tap and the sheet.
    sheet.body.replaceChildren(
      h('p', { class: 'cf-sub', text: t('escapee.alreadyHome') }),
      button({ class: 'cf-btn cf-btn--primary', text: t('common.close'), on: { click: sheet.close } }),
    );
    return;
  }

  sheet.body.appendChild(head(view));
  sheet.body.appendChild(odds(view));
  sheet.body.appendChild(attempt(ctx, view.id, sheet.body, sheet.close));
}

/** The same portrait-and-name head the warning screen opens with. */
function head(view: EscapeeView): HTMLElement {
  return div(
    { class: 'cf-warninghead' },
    h('img', {
      class: 'cf-warninghead__img',
      attrs: { src: view.sprite, alt: t('bird.portraitAlt', { name: view.name }) },
    }),
    div(
      { class: 'cf-stack cf-stack--tight' },
      h('h1', { class: 'cf-display', text: t('escapee.headline', { name: view.name }) }),
      h('span', {
        class: 'cf-sub',
        text: t('warning.breedLine', { breed: view.breed, weeks: view.weeks }),
      }),
    ),
  );
}

function odds(view: EscapeeView): HTMLElement {
  return div(
    { class: 'cf-card cf-stack cf-stack--tight' },
    h('span', { class: 'cf-label', text: t('escapee.oddsLabel') }),
    h('p', { class: 'cf-small', text: t(view.oddsKey) }),
  );
}

function attempt(
  ctx: ScreenContext<HTMLElement>,
  chickenId: string,
  body: HTMLElement,
  close: () => void,
): HTMLButtonElement {
  return button(
    {
      class: 'cf-actionrow cf-actionrow--primary',
      on: { click: () => tryCatch(ctx, chickenId, body, close) },
    },
    icon({ shapes: CATCH_ICON, size: 28, tone: 'white', strokeWidth: 2.4 }),
    div(
      { class: 'cf-stack cf-stack--tight cf-grow' },
      h('span', { class: 'cf-actionrow__title', text: t('escapee.try') }),
      h('span', { class: 'cf-actionrow__sub', text: t('escapee.trySub') }),
    ),
  );
}

function tryCatch(
  ctx: ScreenContext<HTMLElement>,
  chickenId: string,
  body: HTMLElement,
  close: () => void,
): void {
  const { store, shell } = ctx.services;
  const view = selectEscapee(store.getState(), chickenId);
  if (!view) {
    close();
    return;
  }

  const result = catchEscapee(store, view.id);
  const message = t(result.messageKey ?? 'escapee.missed', {
    ...(result.messageParams ?? {}),
    name: view.name,
  });

  const retry = button({
    class: 'cf-btn cf-btn--primary',
    text: result.caught ? t('common.close') : t('escapee.tryAgain'),
    on: { click: result.caught ? close : () => tryCatch(ctx, chickenId, body, close) },
  });

  body.replaceChildren(
    div(
      { class: `cf-verdict ${result.caught ? 'cf-verdict--good' : 'cf-verdict--bad'}` },
      h('p', {
        class: 'cf-verdict__title',
        text: t(result.caught ? 'escapee.caught' : 'escapee.missed'),
      }),
      h('p', { class: 'cf-verdict__body', text: message }),
    ),
    retry,
  );

  if (result.caught) {
    playChoreDone(store);
    shell.toast(message);
  }
}
